import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Button, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Chip, IconButton, Typography, Autocomplete, CircularProgress,
  Box, Badge, Tooltip, Divider, TextField, InputBase,
  useTheme as useMuiTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AdminPanelSettings as AdminIcon,
  PersonAdd as AddIcon,
  PersonRemove as RemoveIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import chatService from '../../services/chat.service';
import { useChat } from '../../contexts/ChatContext';
import { useAppSelector } from '../../hooks/redux.hooks';
import { UserSummary, ConversationMember } from '../../types/chat.types';
import { useLanguage } from '../../contexts/LanguageContext';
import { chatTranslations } from './chatTranslations';
import { toast } from 'react-toastify';

const AVATAR_COLORS = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#a18cd1,#fbc2eb)',
];
const avatarGradient = (name: string) =>
  AVATAR_COLORS[(name.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

interface Props {
  open: boolean;
  onClose: () => void;
  conversationId?: number;
  existingMembers?: ConversationMember[];
  mode?: 'create' | 'manage';
}

const GroupManageDialog: React.FC<Props> = ({
  open, onClose, conversationId, existingMembers = [], mode = 'create',
}) => {
  const isSuperAdmin  = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
  const currentUserId = useAppSelector(s => s.auth.user?.userId ?? 0);
  const { loadConversations } = useChat();
  const { language } = useLanguage();
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = chatTranslations[language];
  const isRTL = language === 'ar';

  const [groupName, setGroupName]       = useState('');
  const [allUsers, setAllUsers]         = useState<UserSummary[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserSummary[]>([]);
  const [members, setMembers]           = useState<ConversationMember[]>(existingMembers);
  const [memberSearch, setMemberSearch] = useState('');
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    chatService.getUsers().then(setAllUsers).finally(() => setLoading(false));
    if (mode === 'manage') setMembers(existingMembers);
  }, [open]);

  const handleCreate = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) return;
    try {
      setSaving(true);
      await chatService.createGroup(groupName.trim(), selectedUsers.map(u => u.userId));
      await loadConversations();
      onClose();
    } catch {
      toast.error(t.failedToSend);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (user: UserSummary) => {
    if (!conversationId) return;
    try {
      await chatService.addMember(conversationId, user.userId);
      setMembers(prev => [...prev, {
        userId: user.userId, fullName: user.fullName,
        userName: user.userName, role: 'Member', isOnline: user.isOnline,
      }]);
    } catch {
      toast.error(t.failedAddMember);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!conversationId) return;
    try {
      await chatService.removeMember(conversationId, userId);
      setMembers(prev => prev.filter(m => m.userId !== userId));
    } catch {
      toast.error(t.failedRemoveMember);
    }
  };

  const handleDelegateAdmin = async (userId: number) => {
    if (!conversationId) return;
    try {
      await chatService.delegateAdmin(conversationId, userId);
      setMembers(prev => prev.map(m => m.userId === userId ? { ...m, role: 'Admin' } : m));
    } catch {
      toast.error(t.failedMakeAdmin);
    }
  };

  const handleRevokeAdmin = async (userId: number) => {
    if (!conversationId) return;
    try {
      await chatService.revokeAdmin(conversationId, userId);
      setMembers(prev => prev.map(m => m.userId === userId ? { ...m, role: 'Member' } : m));
    } catch {
      toast.error(t.failedRemoveAdmin);
    }
  };

  const nonMembers = allUsers.filter(u => !members.some(m => m.userId === u.userId));
  const filteredNonMembers = nonMembers.filter(u =>
    !memberSearch ||
    u.fullName.toLowerCase().includes(memberSearch.toLowerCase()) ||
    u.userName.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1600 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* ── Header ── */}
      <Box sx={{
        background: isDark
          ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 100%)'
          : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        px: 2.5, py: 2,
        display: 'flex', alignItems: 'center', gap: 1.5,
        direction: isRTL ? 'rtl' : 'ltr',
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2, flexShrink: 0,
          bgcolor: 'rgba(255,255,255,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <GroupIcon sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, color: 'white' }}>
          {mode === 'create' ? t.createGroupChat : t.groupSettings}
        </Typography>
        <Tooltip title={t.close} arrow slotProps={{ popper: { sx: { zIndex: 9999 } } }}>
          <IconButton size="small" onClick={onClose}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent sx={{ p: 0, direction: isRTL ? 'rtl' : 'ltr' }}>

        {/* ── CREATE MODE ── */}
        {mode === 'create' ? (
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label={t.groupName}
              size="small"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Autocomplete
                multiple
                options={allUsers}
                getOptionLabel={u => `${u.fullName} (${u.userName})`}
                value={selectedUsers}
                onChange={(_, val) => setSelectedUsers(val)}
                isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
                componentsProps={{ popper: { style: { zIndex: 9999 } } }}
                renderInput={(params) => (
                  <TextField {...params} label={t.addMembers} size="small" InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />
                )}
                renderOption={(props, u) => (
                  <Box component="li" {...props} key={u.userId} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                    borderRadius: 1, mx: 0.5, my: 0.25,
                  }}>
                    <Badge
                      variant="dot"
                      color={u.isOnline ? 'success' : 'default'}
                      anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
                      sx={{ '& .MuiBadge-dot': { width: 12, height: 12, border: '2px solid', borderColor: 'background.paper', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' } }}
                    >
                      <Avatar sx={{
                        width: 34, height: 34, fontSize: 13, fontWeight: 700,
                        background: avatarGradient(u.fullName),
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}>
                        {u.fullName.charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} fontSize="0.84rem" noWrap>
                        {u.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize="0.7rem" noWrap>
                        @{u.userName}
                      </Typography>
                    </Box>
                    {u.isOnline && (
                      <Chip
                        label={t.online}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.6rem', borderRadius: 1, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Box>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((u, i) => (
                    <Chip
                      {...getTagProps({ index: i })}
                      label={u.fullName}
                      size="small"
                      avatar={
                        <Avatar sx={{ background: avatarGradient(u.fullName), fontSize: 10, fontWeight: 700 }}>
                          {u.fullName.charAt(0)}
                        </Avatar>
                      }
                      sx={{ borderRadius: 2 }}
                    />
                  ))
                }
                ListboxProps={{
                  sx: {
                    maxHeight: 240,
                    '& .MuiAutocomplete-option': { p: 0 },
                  }
                }}
              />
            )}
          </Box>
        ) : (

          /* ── MANAGE MODE ── */
          <>
            {/* Current Members */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t.membersCount} ({members.length})
              </Typography>
            </Box>

            <List dense sx={{ px: 1, pb: 0 }}>
              {members.map(m => (
                <ListItem
                  key={m.userId}
                  sx={{ borderRadius: 2, mb: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {m.role !== 'Admin' && isSuperAdmin && (
                        <Tooltip title={t.makeAdmin} arrow placement="top" slotProps={{ popper: { sx: { zIndex: 9999 } } }}>
                          <span>
                            <IconButton size="small" onClick={() => handleDelegateAdmin(m.userId)}
                              sx={{ '&:hover': { color: 'primary.main' } }}>
                              <AdminIcon fontSize="small" color="primary" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {m.role === 'Admin' && isSuperAdmin && m.userId !== currentUserId && (
                        <Tooltip title={t.removeAdmin} arrow placement="top" slotProps={{ popper: { sx: { zIndex: 9999 } } }}>
                          <span>
                            <IconButton size="small" onClick={() => handleRevokeAdmin(m.userId)}>
                              <AdminIcon fontSize="small" color="disabled" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {m.userId !== currentUserId && (
                        <Tooltip title={t.removeFromGroup} arrow placement="top" slotProps={{ popper: { sx: { zIndex: 9999 } } }}>
                          <span>
                            <IconButton size="small" onClick={() => handleRemoveMember(m.userId)}
                              sx={{ '&:hover': { color: 'error.main' } }}>
                              <RemoveIcon fontSize="small" color="error" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Badge
                      variant="dot"
                      color={m.isOnline ? 'success' : 'default'}
                      anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'left' : 'right' }}
                      sx={{ '& .MuiBadge-dot': { width: 12, height: 12, border: '2px solid', borderColor: 'background.paper', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' } }}
                    >
                      <Avatar sx={{
                        width: 32, height: 32, fontSize: 12, fontWeight: 700,
                        background: avatarGradient(m.fullName),
                      }}>
                        {m.fullName.charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" component="div" fontWeight={600} fontSize="0.82rem">
                          {m.fullName}
                        </Typography>
                        {m.role === 'Admin' && (
                          <Chip
                            label={t.admin}
                            size="small"
                            color="primary"
                            sx={{ height: 16, fontSize: '0.6rem', borderRadius: 1, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                      </Box>
                    }
                    secondary={m.userName}
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ variant: 'caption', fontSize: '0.7rem' }}
                  />
                </ListItem>
              ))}
            </List>

            {/* Add Members section */}
            {nonMembers.length > 0 && (
              <>
                <Divider sx={{ mx: 2, my: 1 }} />
                <Box sx={{ px: 2.5, pb: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {t.addMembers}
                  </Typography>
                </Box>

                {/* Search non-members */}
                <Box sx={{ px: 2, pb: 1 }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    bgcolor: 'action.hover', borderRadius: 3, px: 1.5, py: 0.5,
                    border: '1px solid', borderColor: 'divider',
                    '&:focus-within': { borderColor: 'primary.main' },
                    transition: 'all 0.2s',
                  }}>
                    <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                    <InputBase
                      fullWidth
                      placeholder={t.searchUsers}
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      sx={{ fontSize: '0.8rem' }}
                    />
                  </Box>
                </Box>

                <List dense sx={{ px: 1, pb: 1, maxHeight: 160, overflowY: 'auto' }}>
                  {filteredNonMembers.slice(0, 20).map(u => (
                    <ListItem
                      key={u.userId}
                      sx={{ borderRadius: 2, mb: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
                      secondaryAction={
                        <Tooltip title={t.addMembers} arrow placement="top" slotProps={{ popper: { sx: { zIndex: 9999 } } }}>
                          <span>
                            <IconButton size="small" onClick={() => handleAddMember(u)}
                              sx={{
                                background: 'linear-gradient(135deg,#4facfe,#00f2fe)',
                                color: 'white', width: 26, height: 26,
                                '&:hover': { transform: 'scale(1.1)' },
                                transition: 'transform 0.15s',
                              }}>
                              <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{
                          width: 28, height: 28, fontSize: 11, fontWeight: 700,
                          background: avatarGradient(u.fullName),
                        }}>
                          {u.fullName.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.fullName}
                        secondary={u.userName}
                        primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption', fontSize: '0.68rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{
        px: 2.5, py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        direction: isRTL ? 'rtl' : 'ltr',
        gap: 1,
      }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ borderRadius: 2, minWidth: 80 }}>
          {mode === 'create' ? t.cancel : t.close}
        </Button>
        {mode === 'create' && (
          <Button
            variant="contained"
            size="small"
            onClick={handleCreate}
            disabled={saving || !groupName.trim() || selectedUsers.length === 0}
            sx={{
              borderRadius: 2, minWidth: 110,
              background: isDark
                ? 'linear-gradient(135deg, #2d3a5c 0%, #3d4a6c 100%)'
                : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              '&:hover': { background: isDark
                ? 'linear-gradient(135deg, #3d4a6c 0%, #4d5a7c 100%)'
                : 'linear-gradient(135deg, #3d9be8 0%, #00d4e0 100%)' },
              '&.Mui-disabled': { background: 'action.disabledBackground' },
            }}
          >
            {saving ? t.creating : t.createGroupBtn}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GroupManageDialog;
